import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Switch } from "antd";
import ButtonAndAddCTA from "../ButtonAndAddCTA";
import CustomInput from "../../../../Core/Components/CustomInput";
import CloseIcon from "../../../../Common/Svgs/CloseIcon";
import FilterItem from "../../../../Core/Components/FilterItem";
import MoreFarmerFilters from "./MoreFarmerFilters";
import { LOCATION_TYPE } from "../../../Location/Data";
import usePermission, {
  useTenantPermission,
} from "../../../../Core/hooks/usePermission";
import { PERMISSIONS, TENANT_PERMISSIONS } from "../../../Login/constants";
import { updateUsers } from "../../../../Redux/Slices/users.slice";
import SearchAndSelectFarmers from "./SearchAndSelectFarmers";

const PartnerHeader = ({
  search,
  setSearch,
  filters,
  setFilters,
  setCurrentPage,
}) => {
  const dispatch = useDispatch();

  const { allLocations } = useSelector((state) => state.locations);

  // tenant level permission
  const hasTenantLocationPermission = useTenantPermission([
    TENANT_PERMISSIONS.LOCATION_MODULE_ACCESS,
  ]);

  // user level permission
  const hasFarmerExportPermission = usePermission([PERMISSIONS.PARTNER_EXPORT]);

  // get all the hubs
  const hubFilters = allLocations
    .filter((item) => item.type === LOCATION_TYPE.HUB_STATION)
    .map((location) => ({
      id: location.id,
      value: location.name,
    }));

  // get all collection and aggregation points
  const collectionPointFilters = allLocations
    .filter((item) =>
      [LOCATION_TYPE.COLLECTION, LOCATION_TYPE.AGGREGATION].includes(item.type),
    )
    .map((location) => ({
      id: location.id,
      value: location.name,
    }));

  const [focus, setFocus] = useState(false);

  const onSelect = (key, value) => {
    // whenever applying the filter, the page should be defaulted back to first page
    setCurrentPage(1);

    if (filters[key].includes(value)) {
      const newFilters = filters[key].filter((item) => item !== value);
      setFilters({ ...filters, [key]: newFilters });
    } else {
      setFilters({ ...filters, [key]: [...filters[key], value] });
    }
  };

  return (
    <div className="bg-background-white">
      <ButtonAndAddCTA />

      <div className="px-6 py-3.5 flex items-center justify-between">
        <div className="flex items-center gap-x-3">
          <CustomInput
            intent="search"
            prefix={
              <img src="/assets/icons/search.svg" alt="" className="mr-2" />
            }
            value={search}
            focus={focus}
            border={focus ? "success" : "none"}
            placeholder={focus ? null : "Search Partners"}
            onChange={(e) => setSearch(e.target.value)}
            onFocus={() => setFocus(true)}
            onBlur={() => setFocus(false)}
            suffix={
              search.length > 0 ? (
                <CloseIcon
                  className="w-4 h-4 cursor-pointer stroke-dark-grey-text"
                  onClick={() => {
                    setFocus(false);
                    setSearch("");
                  }}
                />
              ) : null
            }
          />
          <div className="flex items-center">
            {hasTenantLocationPermission && (
              <FilterItem
                label="Hub Station"
                identifier="hub"
                contentData={hubFilters}
                onSelect={onSelect}
                selectedValues={filters.hub}
                className="max-h-[200px] overflow-y-scroll"
                enableSearch
              />
            )}

            {hasTenantLocationPermission && (
              <FilterItem
                label="Collection Point"
                identifier="collectionPoint"
                contentData={collectionPointFilters}
                onSelect={onSelect}
                selectedValues={filters.collectionPoint}
                className="max-h-[200px] overflow-y-scroll"
                enableSearch
              />
            )}

            <SearchAndSelectFarmers filters={filters} setFilters={setFilters} />

            <MoreFarmerFilters filters={filters} setFilters={setFilters} />

            <div className="bg-light-grey w-[1px] h-5 mx-1" />
          </div>

          <div className="flex items-center gap-x-2">
            <Switch
              checked={+filters.retired === 1}
              onChange={(checked) => {
                setCurrentPage(1);
                setFilters({ ...filters, retired: checked ? 1 : 0 });
              }}
              size="small"
              className={+filters.retired === 1 ? "bg-primary" : "bg-[#949494]"}
            />
            <p className="text-xs font-medium font-poppins text-dark-grey-text whitespace-nowrap">
              Show Only retired farmers
            </p>
          </div>
        </div>

        {hasFarmerExportPermission && (
          <button
            type="button"
            className="px-3 py-[11px] rounded-md bg-primary text-white"
            onClick={() => {
              dispatch(
                updateUsers({
                  key: "exportFarmerModal",
                  value: true,
                }),
              );
            }}
          >
            <p className="font-poppins font-medium text-xs">Export as CSV</p>
          </button>
        )}
      </div>
    </div>
  );
};

export default PartnerHeader;
